import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RowDragEvent } from 'ag-grid-community';
import { useMutation, useQuery } from '@apollo/react-hooks';
import {
    REORDER_USER_GUIDE_SCOPE_GROUPS,
    REORDER_USER_GUIDES_IN_SCOPES,
    USER_GUIDE_ARTICLES_FOR_ORDERING,
    USER_GUIDE_ORDER_ALL_KEY,
    USER_GUIDE_SCOPES,
} from '../../../../gql/userGuideScopes';
import { ArticleSummary, UserGuideScope } from '../../../../shared/types';
import { filterArrayOfObjectsBy } from '../../../../utils/sorting.utils';
import {
    MessageTypes,
    useNotification,
} from '../../../providers/NotificationProvider';
import { groupUserGuideScopesByName } from './utils/scopeGrouping';
import { areSameOrder, sortByExplicitOrder } from './utils/orderUtils';
import UserGuideScopesTable from './components/UserGuideScopesTable';
import UserGuideOrderingTable from './components/UserGuideOrderingTable';

type UserGuideScopesQueryData = {
    userGuideScopes: UserGuideScope[];
};

const UserGuideScopeList = (): JSX.Element => {
    const { showMessage } = useNotification();
    const {
        loading,
        error,
        data,
        refetch: refetchUserGuideScopes,
    } = useQuery<UserGuideScopesQueryData>(USER_GUIDE_SCOPES, {
        fetchPolicy: 'network-only',
    });
    const [reorderScopeGroups, { loading: savingScopeOrder }] = useMutation(
        REORDER_USER_GUIDE_SCOPE_GROUPS,
        {
            refetchQueries: [{ query: USER_GUIDE_SCOPES }],
        }
    );
    const [reorderUserGuidesInScopes, { loading: savingGuideOrder }] =
        useMutation(REORDER_USER_GUIDES_IN_SCOPES);

    const [searchString, setSearchString] = useState('');
    const [pendingScopeOrderIds, setPendingScopeOrderIds] = useState<string[]>(
        []
    );
    const [selectedScopeId, setSelectedScopeId] = useState<string>(
        USER_GUIDE_ORDER_ALL_KEY
    );
    const [pendingGuideOrderIds, setPendingGuideOrderIds] = useState<string[]>(
        []
    );

    const groupedItems = useMemo(
        () => groupUserGuideScopesByName(data?.userGuideScopes ?? []),
        [data?.userGuideScopes]
    );

    const selectedScopeGroup = useMemo(
        () => groupedItems.find((item) => item._id === selectedScopeId),
        [groupedItems, selectedScopeId]
    );

    const selectedUserGuideScopes = useMemo(
        () =>
            selectedScopeId === USER_GUIDE_ORDER_ALL_KEY
                ? [
                      USER_GUIDE_ORDER_ALL_KEY,
                      ...Array.from(
                          new Set(
                              groupedItems.flatMap((group) =>
                                  group.scopes.filter(Boolean)
                              )
                          )
                      ),
                  ]
                : (selectedScopeGroup?.scopes ?? []),
        [groupedItems, selectedScopeGroup?.scopes, selectedScopeId]
    );

    const {
        data: userGuideArticlesData,
        loading: userGuideArticlesLoading,
        refetch: refetchUserGuideArticles,
    } = useQuery<{
        userGuideArticlesForOrdering: ArticleSummary[];
    }>(USER_GUIDE_ARTICLES_FOR_ORDERING, {
        fetchPolicy: 'network-only',
        variables: {
            scopeKeys: selectedUserGuideScopes,
        },
    });

    useEffect(() => {
        const refreshUserGuides = () => {
            refetchUserGuideScopes();
            refetchUserGuideArticles();
        };

        window.addEventListener('focus', refreshUserGuides);

        return () => window.removeEventListener('focus', refreshUserGuides);
    }, [refetchUserGuideArticles, refetchUserGuideScopes]);

    const persistedScopeOrderIds = useMemo(
        () => groupedItems.map((item) => String(item._id)),
        [groupedItems]
    );

    const displayedScopeOrderRows = useMemo(
        () =>
            pendingScopeOrderIds.length
                ? sortByExplicitOrder(groupedItems, pendingScopeOrderIds)
                : groupedItems,
        [groupedItems, pendingScopeOrderIds]
    );

    const hasPendingScopeOrderChanges = useMemo(
        () =>
            pendingScopeOrderIds.length > 0 &&
            !areSameOrder(pendingScopeOrderIds, persistedScopeOrderIds),
        [pendingScopeOrderIds, persistedScopeOrderIds]
    );

    const filteredAndSorted = useMemo(
        () =>
            filterArrayOfObjectsBy(displayedScopeOrderRows, searchString, [
                'name',
                'scope',
            ]),
        [displayedScopeOrderRows, searchString]
    );

    const persistedGuideOrderIds = useMemo(
        () =>
            (userGuideArticlesData?.userGuideArticlesForOrdering ?? []).map(
                (item) => String(item._id)
            ),
        [userGuideArticlesData?.userGuideArticlesForOrdering]
    );

    const displayedGuideRows = useMemo(() => {
        const currentRows =
            userGuideArticlesData?.userGuideArticlesForOrdering ?? [];

        return pendingGuideOrderIds.length
            ? sortByExplicitOrder(currentRows, pendingGuideOrderIds)
            : currentRows;
    }, [
        pendingGuideOrderIds,
        userGuideArticlesData?.userGuideArticlesForOrdering,
    ]);

    const hasPendingGuideOrderChanges = useMemo(
        () =>
            pendingGuideOrderIds.length > 0 &&
            !areSameOrder(pendingGuideOrderIds, persistedGuideOrderIds),
        [pendingGuideOrderIds, persistedGuideOrderIds]
    );

    const onScopeOrderRowDragEnd = useCallback((event: RowDragEvent) => {
        const reorderedIds: string[] = [];

        event.api.forEachNode((node) => {
            if (node.data?._id) {
                reorderedIds.push(String(node.data._id));
            }
        });

        setPendingScopeOrderIds(reorderedIds);
    }, []);

    const onSaveScopeOrder = useCallback(async () => {
        if (!hasPendingScopeOrderChanges) return;

        try {
            const reorderedRows = sortByExplicitOrder(
                groupedItems,
                pendingScopeOrderIds
            );

            await reorderScopeGroups({
                variables: {
                    groupNames: reorderedRows.map((item) => item.name),
                },
            });

            setPendingScopeOrderIds([]);
            showMessage(
                'User guide scope ordering has been saved.',
                MessageTypes.info
            );
        } catch {
            showMessage(
                'Failed to save user guide scope ordering. Please try again.',
                MessageTypes.error
            );
        }
    }, [
        groupedItems,
        hasPendingScopeOrderChanges,
        pendingScopeOrderIds,
        reorderScopeGroups,
        showMessage,
    ]);

    const onGuideOrderRowDragEnd = useCallback((event: RowDragEvent) => {
        const reorderedIds: string[] = [];

        event.api.forEachNode((node) => {
            if (node.data?._id) {
                reorderedIds.push(String(node.data._id));
            }
        });

        setPendingGuideOrderIds(reorderedIds);
    }, []);

    const onSaveGuideOrder = useCallback(async () => {
        if (!hasPendingGuideOrderChanges) return;

        const scopeKeys =
            selectedScopeId === USER_GUIDE_ORDER_ALL_KEY
                ? [USER_GUIDE_ORDER_ALL_KEY]
                : (selectedScopeGroup?.scopes ?? []);

        if (!scopeKeys.length) {
            showMessage(
                'Please select a valid scope before saving user guide order.',
                MessageTypes.warning
            );
            return;
        }

        try {
            await reorderUserGuidesInScopes({
                variables: {
                    orders: [
                        {
                            scopeKeys,
                            articleIds: pendingGuideOrderIds,
                        },
                    ],
                },
            });

            await refetchUserGuideArticles();
            setPendingGuideOrderIds([]);
            showMessage(
                'User guide ordering has been saved.',
                MessageTypes.info
            );
        } catch {
            showMessage(
                'Failed to save user guide ordering. Please try again.',
                MessageTypes.error
            );
        }
    }, [
        hasPendingGuideOrderChanges,
        pendingGuideOrderIds,
        reorderUserGuidesInScopes,
        refetchUserGuideArticles,
        selectedScopeGroup?.scopes,
        selectedScopeId,
        showMessage,
    ]);

    const onSelectScope = useCallback((scopeId: string) => {
        setSelectedScopeId(scopeId);
        setPendingGuideOrderIds([]);
    }, []);

    return (
        <>
            <UserGuideScopesTable
                items={filteredAndSorted}
                loading={loading}
                error={error}
                searchString={searchString}
                onQueryStringChange={setSearchString}
                onRowDragEnd={onScopeOrderRowDragEnd}
                hasPendingChanges={hasPendingScopeOrderChanges}
                saving={savingScopeOrder}
                onSaveOrder={onSaveScopeOrder}
            />

            <UserGuideOrderingTable
                items={displayedGuideRows}
                loading={userGuideArticlesLoading}
                groups={groupedItems}
                selectedScopeId={selectedScopeId}
                onSelectScope={onSelectScope}
                onRowDragEnd={onGuideOrderRowDragEnd}
                hasPendingChanges={hasPendingGuideOrderChanges}
                saving={savingGuideOrder}
                onSave={onSaveGuideOrder}
            />
        </>
    );
};

export default UserGuideScopeList;
